<template>
  <div class="task-create text-dark">
    <DModal @close="close" :show-close="false">
      <div class="task-create__modal" slot="insertComponent">
        <div class="task-create__header" :class="{'task-create__header--create-subject': canSchedule}"
             :style="{'backgroundColor': color}">
          <div class="task-create__header-main">
            <h3>
              <span v-if="name.length < 1">{{ i18n[locale]['create_task'] }}</span>
              <span v-else>{{ name }}</span>
            </h3>
            <DDropdown
              v-if="canCopyToSharedCalendar"
              class="task-create__header-menu"
              :isBottomStart="false"
              :propOffset="[0, 8]">
              <ul slot="list" class="task-create__copy-calendar-list">
                <li class="task-create__menu-heading">{{ i18n[locale]['copy_to_shared_calendar'] }}</li>
                <li
                  v-for="calendar in copyCalendarOptions"
                  :key="calendar.id"
                  @click="copyTaskToSharedCalendar(calendar.id)">
                  <span class="icon-clipboard2"></span> {{ calendar.name }}
                </li>
              </ul>
            </DDropdown>
          </div>
          <div class="task-create__close icon__bg-circle" @click="close">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <form id="task-create" class="task-create__body" @submit.prevent>
          <div v-if="isLoadingFreshData" class="task-create__loading-overlay">
            <div class="do-spinner"></div>
          </div>
          <div class="task-create__form">
            <template v-if="!quickCreate">
              <div class="input-group__flex input-group__flex--top-row">
                <div class="input-group input-group__title input-flex-2">
                  <label>{{ i18n[locale]['task_title'] }}</label>
                  <input type="text"
                         class="d-input"
                         v-model="name"
                         :placeholder="i18n[locale]['ex_task']"
                         @keyup="removeErrors('name')"
                         ref="name"
                         :class="{'input--error': formErrors['name']}">
                  <p class="input-error" v-if="formErrors['name']">{{ formErrors['name'][locale] }}</p>
                </div>
                <div class="input-group input-group__hours-top">
                  <label>{{ i18n[locale]['hours'] }} </label>
                  <input type="number"
                         class="d-input"
                         ref="inputLessonCount"
                         v-model.number="hours"
                         @focus="selectAllOnFocus($event)"
                         @blur="removeErrors('hours')"
                         :class="{'input--error': formErrors['hours']}">
                  <p class="input-error" v-if="formErrors['hours']">{{ formErrors['hours'][locale] }}</p>
                </div>
                <div class="input-group input-group__preparation" v-if="isShowNorwegianPreparationDropdown">
                  <label>Årsramme</label>
                  <input type="number" class="d-input" v-model="preparation"/>
                </div>
                <DkPreparationFactorControl
                  v-else-if="isShowDanishPreparationFactorField && !isDanishPrepBinaryOnOwnRow"
                  v-model="preparation"
                  :subject-type="subjectType"
                  :school-prep-factor="schoolSettings.prepFactor"
                  :labels="danishPreparationControlLabels"
                  input-class="d-input"
                  wrap-class="input-group input-group__preparation">
                </DkPreparationFactorControl>
              </div>
              <div class="input-group" v-if="isDanishPrepBinaryOnOwnRow">
                <DkPreparationFactorControl
                  v-model="preparation"
                  :subject-type="subjectType"
                  :school-prep-factor="schoolSettings.prepFactor"
                  :labels="danishPreparationControlLabels"
                  input-class="d-input"
                  wrap-class="input-group input-group__preparation dk-prep-factor-full-width">
                </DkPreparationFactorControl>
              </div>
            </template>
            <div class="input-group">
              <label><span class="icon-user"></span> {{ i18n[locale]['add_user'] }}</label>
              <DSelectCalendarIds itemType="USER"
                                  :preSelectedItems="calendars"
                                  :showTags="true"
                                  :itemsThatCantBeDeleted="usersWithEvents"
                                  @update="setSelectedUsers">
              </DSelectCalendarIds>
            </div>
            <template v-if="!quickCreate">
            <div class="input-group">
              <div class="create-as-subject">
                <div class="create-as-subject__inner" @click.stop="toggleDateFields">
                  <AppToggle :propOn="isShowDateFields" :allowChanges="false"></AppToggle>
                  <label style="margin-bottom: 0">{{ i18n[locale]['show_dates_on_overview'] }}</label>
                </div>
              </div>
            </div>
            <transition name="dropdown">
              <div class="input-group__flex" v-if="isShowDateFields">
                <div class="input-group">
                  <label>{{ i18n[locale]['from_date'] }}</label>
                  <div class="date-field">
                    <DateSelector
                      :date="fromDate || today"
                      :width100="true"
                      @changed="setFromDate">
                    </DateSelector>
                  </div>
                </div>
                <div class="input-group">
                  <label>{{ i18n[locale]['to_date'] }}</label>
                  <div class="date-field">
                    <DateSelector
                      :date="toDate || today"
                      :width100="true"
                      @changed="setToDate">
                    </DateSelector>
                  </div>
                </div>
              </div>
            </transition>
            <div class="input-group" v-if="!editTask">
              <label>{{ i18n[locale]['choose_type'] }}</label>
              <SubjectFormSubjectType calendar-type="user"
                                      :subject-type="subjectType"
                                      @change="setSubjectType"
                                      :isInTasks="true">
              </SubjectFormSubjectType>
            </div>
            </template>
            <div class="input-group" v-if="quickCreate">
              <label>{{ i18n[locale]['hours_as'] }} <span class="pill pill--grey hint--top-right"
                                     aria-label="Vælg briktype for at ændre">{{ hoursType }}</span> </label>
              <div class="input-group__flex">
                <div style="width: 50%">
                  <input type="number" class="d-input" ref="inputLessonCount" v-model.number="hours"
                         @focus="selectAllOnFocus($event)"
                         @blur="removeErrors('hours')"
                         :class="{'input--error': formErrors['hours']}">
                  <p class="input-error" v-if="formErrors['hours']">{{ formErrors['hours'][locale] }}</p>
                </div>
              </div>
            </div>
            <template v-if="!quickCreate">
            <div class="input-group" v-if="!editTask">
              <div class="create-as-subject">
                <div class="create-as-subject__inner" @click.stop="canSchedule = !canSchedule">
                  <AppToggle @changed="setIsCreateAsSubject" :propOn="canSchedule" :allowChanges="false"></AppToggle>
                  <label style="margin-bottom: 0">{{ i18n[locale]['can_be_scheduled'] }}</label>
                </div>
              </div>
            </div>
            <transition name="dropdown">
              <div v-if="canSchedule">
                <div class="input-group">
                  <label>{{ i18n[locale]['duration'] }}</label>
                  <input type="number"
                         class="d-input"
                         ref="inputLessonCount" v-model.number="duration"
                         style="width: 50%"
                         @focus="selectAllOnFocus($event)"
                         @blur="removeErrors('duration')"
                         :class="{'input--error': formErrors['duration']}">
                  <p class="input-error" v-if="formErrors['duration']">{{ formErrors['duration'][locale] }}</p>
                </div>
                <transition name="dropdown">
                  <div class="input-group" v-if="isCreateInOtherCalendarAnOption && !editTask">
                    <label>{{ i18n[locale]['create_in_calendar'] }}</label>
                    <SelectOtherCal :selectedCalendarId="selectedOtherCalendarId"
                                    :calendarType="taskCalendarType"
                                    @changed="setSelectedOtherCalendarId">
                    </SelectOtherCal>
                  </div>
                </transition>
                <div class="input-group" v-if="editTask && getOtherCalendarName(selectedOtherCalendarId)">
                  <label>{{ i18n[locale]['created_in_calendar'] }}</label>
                  <b>{{ getOtherCalendarName(selectedOtherCalendarId) }}</b>
                </div>
                <div class="input-group">
                  <label>{{ i18n[locale]['choose_color'] }}</label>
                  <color-picker @changedColor="setColor" :initialColor="color"></color-picker>
                </div>
              </div>
            </transition>
            </template>
            <div class="input-group input-group__submit task-create__footer">
              <div class="input-group__submit__left">
                <template v-if="editTask">
                  <button v-if="usersWithEvents.length < 1"
                          class="hint--right bttn--warning"
                          aria-label="Slet opgaven"
                          @click="deleteTask">
                    <span class="icon-bin"></span>
                  </button>
                  <div class="hint--right" v-else :aria-label="i18n[locale]['cannot_be_deleted']">
                    <button class="bttn bttn--disabled">
                      <span class="icon-bin"></span>
                    </button>
                  </div>
                </template>
                <button class="do-bttn" @click="close">{{ i18n[locale]['cancel'] }}</button>
              </div>
              <div class="input-group__submit__right">
                <template v-if="!editTask">
                  <button class="do-bttn"
                          :class="{'do-bttn--submit--loading': isSubmittingTask}"
                          @click="createTask(true)">{{
                      i18n[locale]['save_and_new']
                    }}
                  </button>
                  <button class="do-bttn do-bttn--submit"
                          :class="{'do-bttn--submit--loading': isSubmittingTask}"
                          @click="createTask(false)"
                          :content="saveKeyTooltip"
                          v-tippy="{
                            placement: 'top',
                            zIndex: 12312312312312
                          }">{{
                      i18n[locale]['save_and_close']
                    }}
                  </button>
                </template>
                <button class="do-bttn do-bttn--submit"
                        :class="{'do-bttn--submit--loading': isSubmittingTask}"
                        v-if="editTask"
                        @click="updateTask"
                        :content="saveKeyTooltip"
                        v-tippy="{
                          placement: 'top',
                          zIndex: 12312312312312
                        }">{{
                    i18n[locale]['update_task']
                  }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </DModal>
    <div class="bg-overlay animatedFast fadeIn"></div>
  </div>
</template>

<script>
import DSelectCalendarIds from '../DSelect/DSelectCalsIds'
import DModal from '../common/DModal'
import SubjectFormSubjectType from '../Subjects/SubjectFormSubjectType'
import DkPreparationFactorControl from '../Subjects/DkPreparationFactorControl.vue'
import AppToggle from '../common/AppToggle'
import SelectOtherCal from './SubjectsDistributionTasksCreateDropdown'
import DSelect from '../DSelect/DSelect'
import DateSelector from '../datepicker/DateSelector'
import DDropdown from '../common/DDropdown'
import eventbus from '../../eventbus'
import moment from 'moment'
import {
  isSubjectTypeWithDanishPreparationFactor,
  isDanishPreparationBinaryPrepTaskSubjectType,
  schoolHasDanishPreparationFactorConfigured,
  preparationPayloadForSave,
  preparationFromApiForForm
} from '../../utils/dkPreparationFactorUi'

const I18N = {
  da: {
    create_task: 'Opret opgave',
    save_and_close: 'Gem og afslut',
    save_and_new: 'Gem og opret ny opgave',
    update_task: 'Opdatér',
    task_title: 'Opgavens titel',
    ex_task: 'Eks. Tilsyn',
    choose_type: 'Vælg type',
    hours_as: 'Timer som',
    hours: 'Timer',
    add_user: 'Tilføj personale',
    hours_in_school_year_per_person: 'Øvrig tid',
    teaching_hours: 'UV tid',
    cancel: 'Annullér',
    cannot_be_deleted: 'Kan ikke slettes, da der er skemalagte brikker',
    can_be_scheduled: 'Kan skemalægges',
    duration: 'NORMAL VARIGHED (I MINUTTER)',
    create_in_calendar: 'Opret i kalender',
    created_in_calendar: 'Oprettet i kalenderen',
    choose_color: 'Vælg farve',
    teaching: 'Undervisning',
    preparation: 'Forberedelse',
    additional_tasks: 'Øvrige Opgaver',
    available: 'Rådighedstimer',
    club: 'SFO tid',
    locale_pool: 'Lokalpulje',
    forb_faktor: 'Forb. faktor',
    forb_faktor_range_hint: 'Mellem 0 og -1',
    prep_task_extra_toggle: 'Medregnes ud over forberedelse fra forberedelesfaktor',
    prep_task_extra_toggle_hint: 'Fra: opgaven følger skolens forberedelsesfaktor som øvrige brikker. Til: opgavens timer medregnes med faktor 1 oven i forberedelsen fra undervisningsfag.',
    from_date: 'Startdato',
    to_date: 'Slutdato',
    show_dates_on_overview: 'Vis datoer på opgaveoversigten',
    copy_to_shared_calendar: 'Kopiér opgaven til fælles kalender',
    copied_to_shared_calendar: 'Opgaven er kopieret til fælles kalender'
  },
  nb: {
    create_task: 'Opprett oppgave',
    save_and_close: 'Lagre og avslutt',
    save_and_new: 'Lagre og opprett ny oppgave',
    update_task: 'Oppdater',
    task_title: 'Navn på oppgave',
    ex_task: 'F.eks. Tilsyn',
    choose_type: 'Velg type',
    hours_as: 'Timer som',
    hours: 'Timer',
    add_user: 'Legg til underviser(e)',
    hours_in_school_year_per_person: 'Øvrig tid',
    teaching_hours: 'Undervisningstid',
    cancel: 'Annuller',
    cannot_be_deleted: 'Kan ikke slettes, ettersom faget er timeplanlagt.',
    can_be_scheduled: 'Kan timeplanlegges',
    duration: 'STANDARDLENGDE (I MINUTTER)',
    create_in_calendar: 'Opprett i kalender',
    created_in_calendar: 'Opprettet i kalenderen',
    choose_color: 'Velg farge',
    teaching: 'Undervisning',
    preparation: 'Forberedelse',
    additional_tasks: 'Øvrige oppgaver',
    available: 'Rådighetstimer',
    club: 'SFO tid',
    locale_pool: 'Planleggingsdager',
    forb_faktor: 'Forb. faktor',
    forb_faktor_range_hint: 'Mellom 0 og -1',
    prep_task_extra_toggle: 'Tas med i tillegg til forberedelse fra undervisning',
    prep_task_extra_toggle_hint: 'Av: oppgaven følger skolens forberedelsesfaktor som andre brikker. På: oppgavens timer tas med med faktor 1 i tillegg til forberedelse fra undervisningsfag.',
    from_date: 'Startdato',
    to_date: 'Sluttdato',
    show_dates_on_overview: 'Vis dato på oppgaveoversikt',
    copy_to_shared_calendar: 'Kopier oppgaven til felles kalender',
    copied_to_shared_calendar: 'Oppgaven er kopiert til felles kalender'
  },
  en: {
    create_task: 'Add task',
    save_and_close: 'Save and close',
    save_and_new: 'Save and add new task',
    update_task: 'Update',
    task_title: 'Title',
    ex_task: 'eg. non teaching task',
    choose_type: 'Choose type',
    hours_as: 'Hours as',
    hours: 'Hours',
    add_user: 'Add teacher(s)',
    hours_in_school_year_per_person: 'Additional time',
    teaching_hours: 'Teaching hours',
    cancel: 'Cancel',
    cannot_be_deleted: 'Cannot be deleted due to scheduled tasks.',
    can_be_scheduled: 'Can be scheduled',
    duration: 'STANDARD DURATION (IN MINUTES)',
    create_in_calendar: 'Create in calendar',
    created_in_calendar: 'Created in calendar',
    choose_color: 'Choose color',
    teaching: 'Teaching',
    preparation: 'Preparation',
    additional_tasks: 'Additional tasks',
    available: 'Available hours',
    club: 'Club hours',
    locale_pool: 'Custom pool',
    forb_faktor: 'Prep factor',
    forb_faktor_range_hint: 'Between 0 and -1',
    prep_task_extra_toggle: 'Counted in addition to preparation from teaching',
    prep_task_extra_toggle_hint: 'Off: this task uses the school preparation factor like other tiles. On: hours are counted with factor 1 on top of preparation from teaching subjects.',
    from_date: 'Start date',
    to_date: 'End date',
    show_dates_on_overview: 'Show dates on task overview',
    copy_to_shared_calendar: 'Copy task to shared calendar',
    copied_to_shared_calendar: 'Task copied to shared calendar'
  }
}

export default {
  components: {AppToggle, DModal, DSelectCalendarIds, SubjectFormSubjectType, DkPreparationFactorControl, SelectOtherCal, DSelect, DateSelector, DDropdown},
  props: {
    editTask: {
      default: null
    },
    activeFilter: {
      default: null
    },
    prefill: {
      default: null
    },
    quickCreate: {
      type: Boolean,
      default: false
    },
    isLoadingFreshData: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      name: '',
      hours: 0,
      calendars: [],
      subjectType: 'additional_tasks',
      canSchedule: true,
      duration: 60,
      color: '#999999',
      selectedOtherCalendarId: null,
      formErrors: [],
      preparation: null,
      fromDate: null,
      toDate: null,
      isShowDateFields: false
    }
  },
  computed: {
    metaKey () {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      return isMac ? '⌘' : 'CTRL'
    },
    saveKeyTooltip () {
      return '<p class="tooltip-shortcut tooltip-shortcut--double-key"><span>' + this.metaKey + '</span>' + '<span>Enter</span></p>'
    },
    isArbeidstidsplanActivated () {
      return this.$store.getters.isArbeidstidsplanActivated
    },
    schoolSettings () {
      return this.$store.state.schoolSettings || {}
    },
    isSchoolFromDenmark () {
      return this.schoolCountry.toLowerCase() === 'dk'
    },
    isShowNorwegianPreparationDropdown () {
      return this.isArbeidstidsplanActivated && !this.isSchoolFromDenmark
    },
    isShowDanishPreparationFactorField () {
      if (!this.isSchoolFromDenmark || !this.schoolSettings.deactivatePools) {
        return false
      }
      if (!schoolHasDanishPreparationFactorConfigured(this.schoolSettings)) {
        return false
      }
      return isSubjectTypeWithDanishPreparationFactor(this.subjectType)
    },
    danishPreparationControlLabels () {
      const L = this.i18n[this.locale]
      return {
        forb_faktor: L.forb_faktor,
        forb_faktor_range_hint: L.forb_faktor_range_hint,
        prep_task_extra_toggle: L.prep_task_extra_toggle,
        prep_task_extra_toggle_hint: L.prep_task_extra_toggle_hint
      }
    },
    isDanishPrepBinaryOnOwnRow () {
      return this.isShowDanishPreparationFactorField && isDanishPreparationBinaryPrepTaskSubjectType(this.subjectType)
    },
    i18n () {
      return I18N
    },
    schoolCountry () {
      return this.$store.getters.schoolCountry
    },
    isSchoolFromNorway () {
      return this.schoolCountry.toLowerCase() === 'no'
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    otherCalendars () {
      return this.$store.getters.otherCalendars
    },
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash
    },
    localPoolTitle () {
      const title = this.$store.getters.localPoolTitle
      return title || this.i18n[this.locale]['locale_pool']
    },
    hoursType () {
      switch (this.subjectType) {
        case 'person_resource_visible':
        case 'person_resource':
        case 'subject':
          return this.i18n[this.locale]['teaching']
        case 'preparation':
          return this.i18n[this.locale]['preparation']
        case 'additional_tasks':
          return this.i18n[this.locale]['additional_tasks']
        case 'available':
          return this.i18n[this.locale]['available']
        case 'club':
          return this.i18n[this.locale]['club']
        case 'pool':
          return this.localPoolTitle
        case 'with_kids':
          return 'Med børn'
        case 'without_kids':
          return 'Uden børn'
        default:
          return this.subjectType
      }
    },
    isCreateInOtherCalendarAnOption () {
      return this.subjectType === 'without_kids' || this.subjectType === 'preparation' || this.subjectType === 'additional_tasks' || this.subjectType === 'pool' || this.subjectType === 'club'
    },
    taskCalendarType () {
      return this.subjectType === 'club' ? 'club' : 'other'
    },
    copyCalendarOptions () {
      return this.taskCalendarType === 'club' ? this.$store.getters.clubCalendars : this.otherCalendars
    },
    canCopyToSharedCalendar () {
      return !!(
        this.editTask &&
        this.editTask.in_user_calendar &&
        this.canSchedule &&
        this.isCreateInOtherCalendarAnOption &&
        this.copyCalendarOptions &&
        this.copyCalendarOptions.length > 0
      )
    },
    usersWithEvents () {
      if (!this.editTask) {
        return []
      }
      let usersWithEvents = []
      const usersWithEventsHash = {}
      this.editTask.subjects.forEach(subject => {
        if (subject.has_events) {
          if (subject.user_calendars.length > 0) {
            // Is in other calendar
            subject.user_calendars.forEach(calId => {
              if (!usersWithEventsHash[calId]) {
                usersWithEventsHash[calId] = true
                usersWithEvents.push(calId)
              }
            })
          } else {
            // Is in own calendar
            if (!usersWithEventsHash[subject.calendar_id]) {
              usersWithEventsHash[subject.calendar_id] = true
              usersWithEvents.push(subject.calendar_id)
            }
          }
        }
      })
      return usersWithEvents
    },
    theDeletedItemId () {
      return this.$store.state.subjects.theDeletedItemId
    },
    isSubmittingTask () {
      return this.$store.state.subjects.isSubmittingTask
    },
    today () {
      return moment().format('YYYY-MM-DD')
    }
  },
  watch: {
    editTask () {
      if (this.editTask) {
        this.populateFromEditTask()
        if (this.$refs.name) this.$refs.name.focus()
      }
    },
    defaultDuration (val) {
      this.removeErrors('defaultDuration')
    },
    theDeletedItemId () {
      if (this.theDeletedItemId === this.editTask.id) {
        this.close()
      }
    }
  },
  methods: {
    toggleDateFields () {
      this.isShowDateFields = !this.isShowDateFields
      if (this.isShowDateFields) {
        if (!this.fromDate && this.activeSchoolYear) {
          this.fromDate = this.activeSchoolYear.employee_time_start || null
        }
        if (!this.toDate && this.activeSchoolYear) {
          this.toDate = this.activeSchoolYear.employee_time_end || null
        }
      } else {
        this.fromDate = null
        this.toDate = null
      }
    },
    setFromDate (date) {
      const formatted = moment(date).format('YYYY-MM-DD')
      this.fromDate = formatted
      if (this.toDate && formatted > this.toDate) {
        this.toDate = formatted
      }
    },
    setToDate (date) {
      const formatted = moment(date).format('YYYY-MM-DD')
      this.toDate = formatted
      if (this.fromDate && formatted < this.fromDate) {
        this.fromDate = formatted
      }
    },
    selectAllOnFocus (evt) {
      setTimeout(_ => {
        evt.target.select()
      }, 0)
    },
    getOtherCalendarName (calId) {
      const cal = this.allCalendarsInHash[calId]
      return cal && (cal.type === 'other' || cal.type === 'club') ? cal.name : ''
    },
    setSelectedOtherCalendarId (calendarId) {
      this.selectedOtherCalendarId = calendarId
    },
    setSubjectType (type) {
      this.subjectType = type
      if (this.selectedOtherCalendarId) {
        const cal = this.allCalendarsInHash[this.selectedOtherCalendarId]
        if (!this.isCreateInOtherCalendarAnOption || !cal || cal.type !== this.taskCalendarType) {
          this.selectedOtherCalendarId = null
        }
      }
    },
    setColor (color) {
      this.color = color
    },
    setSelectedUsers (users) {
      this.calendars = users.map(user => user.id)
    },
    setIsCreateAsSubject (boolean) {
      this.canSchedule = boolean
      if (!boolean) {
        this.setColor('#999999')
      } else {
        this.$nextTick(_ => {
          if (this.$refs.inputLessonCount) {
            this.$refs.inputLessonCount.focus()
          }
        })
      }
    },
    formValidation () {
      if (this.name.length < 1) {
        this.$set(this.formErrors, 'name', {
          da: 'Opgaven skal have et navn',
          en: 'Task must have a name',
          nb: 'Opgave må ha et navn'
        })
      }
      if (this.duration < 0) {
        this.$set(this.formErrors, 'duration', {
          da: 'Skal være 0 eller højere',
          en: 'Must be zero or greater',
          nb: 'Må være 0 eller høyere'
        })
      }
    },
    removeErrors (title) {
      this.$delete(this.formErrors, title)
    },
    createTask (keepOpen = false) {
      this.formValidation()
      if (Object.keys(this.formErrors).length > 0) {
        return
      }
      let belongsToCalendars = []
      let userCalendars = []
      let inUserCalendar = true
      if (this.selectedOtherCalendarId) {
        inUserCalendar = false
        belongsToCalendars = [this.selectedOtherCalendarId]
        userCalendars = this.calendars
      } else {
        belongsToCalendars = this.calendars
      }
      let newTask = {
        schoolYearId: this.activeSchoolYear.id,
        subjectType: this.subjectType,
        hours: this.hours,
        name: this.name,
        color: this.color,
        defaultDuration: this.duration,
        preparation: this.preparationPayloadForSave(),
        belongsToCalendars: belongsToCalendars,
        userCalendars: userCalendars,
        canSchedule: this.canSchedule,
        inUserCalendar: inUserCalendar,
        fromDate: this.fromDate || null,
        toDate: this.toDate || null
      }
      this.$emit('create', newTask, {keepOpen})
      this.clearForm()
      if (this.$refs.name) this.$refs.name.focus()
    },
    copyTaskToSharedCalendar (calendarId) {
      if (!calendarId || this.isSubmittingTask) {
        return
      }
      this.formValidation()
      if (Object.keys(this.formErrors).length > 0) {
        return
      }
      const schoolYearId = this.editTask.school_year_id || (this.activeSchoolYear && this.activeSchoolYear.id)
      const newTask = {
        schoolYearId: schoolYearId,
        subjectType: this.subjectType,
        hours: this.hours,
        name: this.name,
        color: this.color,
        defaultDuration: this.duration,
        preparation: this.preparationPayloadForSave(),
        belongsToCalendars: [calendarId],
        userCalendars: this.calendars.slice(),
        canSchedule: this.canSchedule,
        inUserCalendar: false,
        fromDate: this.fromDate || null,
        toDate: this.toDate || null
      }
      this.$store.dispatch('createTask', newTask)
      eventbus.toast = {text: this.i18n[this.locale]['copied_to_shared_calendar'], success: true}
      this.$emit('close', newTask)
    },
    updateTask () {
      this.formValidation()
      if (Object.keys(this.formErrors).length > 0) {
        return
      }
      const task = this.editTask
      const userCalIdsCannotDelete = this.usersWithEvents || [] // Sikrer, at der ikke bliver slettet brugere der ikke kan (også dem der har forladt skolen)
      const userCalendars = Array.from(new Set(this.calendars.concat(userCalIdsCannotDelete)))
      let belongsToCalendars = []
      if (!task.in_user_calendar) {
        belongsToCalendars = task.belongs_to_calendars[0]
      } else {
        belongsToCalendars = userCalendars // Fordi userCalendars allerede inkluderer både users og userCalIdsNotOnSchool
      }
      let updatedTask = {
        schoolId: this.editTask['school_id'],
        schoolYearId: this.editTask['school_year_id'],
        id: this.editTask['id'],
        name: this.name,
        color: this.color,
        hours: this.hours,
        defaultDuration: this.duration,
        preparation: this.preparationPayloadForSave(),
        belongsToCalendars: belongsToCalendars,
        userCalendars: userCalendars,
        inUserCalendar: this.editTask['in_user_calendar'],
        fromDate: this.fromDate || null,
        toDate: this.toDate || null
      }
      this.$store.dispatch('updateTask', updatedTask)
      this.$emit('close', updatedTask)
    },
    clearForm () {
      this.formErrors = []
      this.name = ''
      this.hours = 0
      this.duration = 45
      this.preparation = null
      this.color = '#999999'
      this.calendars = []
      this.selectedOtherCalendarId = null
      this.fromDate = null
      this.toDate = null
      this.isShowDateFields = false
    },
    preparationPayloadForSave () {
      return preparationPayloadForSave(this.preparation, this.isShowDanishPreparationFactorField, this.subjectType)
    },
    preparationFromApiForForm (prep) {
      return preparationFromApiForForm(prep, this.isShowDanishPreparationFactorField, this.subjectType)
    },
    populateFromEditTask () {
      if (!this.editTask) return
      this.name = this.editTask.name
      this.hours = this.editTask.hours
      if (this.editTask.subject_type) {
        this.subjectType = this.editTask.subject_type
      }
      this.color = this.editTask.color
      this.canSchedule = this.editTask.can_schedule
      this.duration = this.editTask.default_duration
      if (this.editTask.preparation != null) {
        this.preparation = this.preparationFromApiForForm(this.editTask.preparation)
      } else {
        this.preparation = null
      }
      this.fromDate = this.editTask.from_date || null
      this.toDate = this.editTask.to_date || null
      this.isShowDateFields = !!(this.fromDate || this.toDate)
      this.calendars = []
      if (this.editTask.user_calendar_ids) {
        this.editTask.user_calendar_ids.forEach(calid => {
          this.calendars.push(calid)
        })
      }
      if (!this.editTask.in_user_calendar && this.editTask.belongs_to_calendars) {
        this.selectedOtherCalendarId = this.editTask.belongs_to_calendars[0]
      }
    },
    close () {
      this.clearForm()
      this.canSchedule = true
      this.$emit('close')
    },
    deleteTask () {
      this.editTask['itemType'] = 'task'
      this.$store.commit('setItemToDelete', this.editTask)
      this.$store.commit('setConfirmDeletion', true)
    },
    onHandleKeyboardShortcutSubmit (evt) {
      if (this.isSubmittingTask) {
        return
      }
      if ((evt.ctrlKey || evt.metaKey) && (evt.key === 'Enter' || evt.keyCode === 13)) {
        evt.preventDefault()
        if (this.editTask) {
          this.updateTask()
        } else {
          this.createTask()
        }
      }
    }
  },
  beforeMount () {
    this.locale = window.USER.language ? window.USER.language : 'da'
    if (this.activeFilter) {
      this.subjectType = this.activeFilter
    }
    if (!this.editTask && this.prefill) {
      if (this.prefill.name) this.name = this.prefill.name
      if (this.prefill.color) this.color = this.prefill.color
      if (this.prefill.subject_type) this.subjectType = this.prefill.subject_type
      if (this.prefill.can_schedule != null) this.canSchedule = this.prefill.can_schedule
      if (this.prefill.default_duration) this.duration = this.prefill.default_duration
      if (this.prefill.preparation != null) {
        this.preparation = this.preparationFromApiForForm(this.prefill.preparation)
      }
      if (this.prefill.in_user_calendar === false && this.prefill.belongs_to_calendars && this.prefill.belongs_to_calendars[0]) {
        this.selectedOtherCalendarId = this.prefill.belongs_to_calendars[0]
      }
      if (this.prefill.from_date) this.fromDate = this.prefill.from_date
      if (this.prefill.to_date) this.toDate = this.prefill.to_date
      if (this.prefill.from_date || this.prefill.to_date) this.isShowDateFields = true
    }
    if (this.editTask) {
      this.populateFromEditTask()
    }
  },
  mounted () {
    setTimeout(_ => {
      if (this.$refs.name) this.$refs.name.focus()
    }, 500)
    window.addEventListener('keydown', this.onHandleKeyboardShortcutSubmit)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onHandleKeyboardShortcutSubmit)
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";


.task-create__header {
  padding: 2.5rem 2rem;
  background: #ffffff;
  border-bottom: 1px solid var(--border-color);
  transition: background-color 0.5s;
  border-radius: 4px 4px 0 0;
  color: #ffffff;
  position: relative;
}

.task-create__header-main {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-right: 2.5rem;
}

.task-create__header-main h3 {
  margin: 0;
}

.task-create__header-menu {
  flex-shrink: 0;
}

.task-create__header.task-create__header--create-subject h3,
.task-create__header.task-create__header--create-subject .task-create__close,
.task-create__header.task-create__header--create-subject .task-create__header-menu {
  color: #ffffff;
}

.task-create__copy-calendar-list {
  max-height: min(320px, 50vh);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.task-create__menu-heading {
  pointer-events: none;
  color: #728195;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: default !important;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}

.task-create__menu-heading:hover {
  background-color: #fff !important;
}


.task-create__close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  padding: 8px;
  font-size: 15px;
  cursor: pointer;
  transition: color 0.25s, background-color 0.25s;
}

.task-create__modal {
  min-width: 550px;
  max-width: 550px;
  border-radius: 4px;
}

.task-create__body {
  padding: 2rem 2rem 0 2rem;
  max-height: calc(80vh);
  overflow-y: auto;
  border-radius: 4px;
  position: relative;
}

.task-create__loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.task-create__form .input-group {
  margin-bottom: 1.5rem;
  flex: 1;
}

.task-create__form .input-group:last-child {
  margin-right: 0;
}

.input-group__submit {
  display: flex;
  justify-content: space-between;
}

.input-group__submit__left,
.input-group__submit__right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.task-create__form .task-create__footer {
  position: sticky;
  bottom: 0;
  z-index: 5;
  margin: 0 -2rem;
  padding: 0.75rem 2rem;
  background-color: #f6f9fc;
  border-top: 1px solid #e3e8ee;
}

.input-group__flex {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.input-group__flex--top-row {
  align-items: flex-start;
}

.input-group__title {
  flex: 2;
}

.input-group__hours-top {
  flex: 1;
}

.input-group__preparation {
  flex: 1;
}

.input-group__flex > .flex-1 {
  flex: 1;
}

.input-group input {
  background-color: #ffffff;
}

.lesson-count__edit input[type="number"] {
  margin: 0 0.25rem;
  text-align: center;
}

.button__delete-subject {
  color: var(--danger);
}

.expand-enter-active, .expand-leave-active {
  transition: transform 0.25s ease-in-out, opacity 0.25s ease-in-out;
}

.expand-enter, .expand-leave-to {
  transform: translateX(-0.5rem);
  opacity: 0;
}

.input-group label {
  letter-spacing: .075rem;
  text-transform: uppercase;
  font-size: 0.85rem;
  line-height: 1rem;
  font-weight: 700;
  display: block;
  color: #a2a3a6;
  margin-bottom: 6px;
}

.advanced-settings__link {
  cursor: pointer;
}

.advanced-settings__link:hover {
  text-decoration: underline;
}

.marg-1-right {
  margin-right: 1rem;
}

.create-as-subject {
  display: flex;
  align-items: center;
  user-select: none;
  cursor: pointer;
}

.create-as-subject__inner {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.create-as-subject__inner label {
  margin-bottom: 0;
  margin-left: 10px;
  cursor: pointer;
}

.create-as-subject__inner label:hover {
  color: var(--dark-blue);
}

.d-input {
  background-color: #fff;
  box-shadow: 0 2px 5px -1px rgb(50 50 93 / 25%), 0 1px 3px -1px rgb(0 0 0 / 30%);
  border: 1px solid #edf2f7;
  min-height: 32px;
}

.input-group--flex {
  display: flex;
  align-items: center;
}

.input-group--flex__setting-icon {
  padding-left: 10px;
}

.input-flex-2 {
  flex: 2 !important;
}

.date-field {
  position: relative;
  display: flex;
  align-items: center;
}

.date-field__clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 11px;
  color: #a2a3a6;
  z-index: 1;
  padding: 4px;
}

.date-field__clear:hover {
  color: var(--danger);
}
</style>



// WEBPACK FOOTER //
// SubjectsDistributionTasksCreate.vue?f359f522